<template>
  <div class="change-password-wrap">
    <p>
      Change the password of your Forward Audio account. You'll get a verification mail shortly after changing the password to confirm the change.
    </p>
    <form @submit.prevent="handleChangePassword">
      <Input type="password" label="Current password" v-model="oldPassword"/>
      <Input type="password_strength" label="New password" v-model="newPassword1"/>
      <Input type="password" label="Confirm password" v-model="newPassword2"/>
      <ModalMessage :message="feedbackMessage" :type="feedbackType" />
      <input type="submit" class="fa-btn fa-btn-primary fa-btn-modal" value="Change password" :disabled="!isBasicInputValid">
    </form>    
  </div>
</template>

<script setup>
import { ref, computed, watch, onBeforeMount, onUnmounted } from 'vue';
import APIClient from '/api/apiClient.ts';
import ModalMessage from './ModalMessage.vue';
import { MessageType, ModalTypes } from '@/types/enums.ts';
import { useUIStore } from '@/store/uiStore';
import { CSRFType } from '@/api/apiClient';
import CSRFManager from '~/utils/csrfManager.ts';

const csrfManager = new CSRFManager(CSRFType.CHANGE_PASSWORD);
const csrfToken = computed(() => csrfManager.state.csrfToken);

onBeforeMount(async () => {
  await csrfManager.startFetchingToken();
});

onUnmounted(() => {
  csrfManager.stopFetchingToken();
});

const uiStore = useUIStore();

const apiClient = APIClient.getInstance();

const oldPassword = ref('');
const newPassword1 = ref('');
const newPassword2 = ref('');
const feedbackMessage = ref('');
const feedbackType = ref(MessageType.INFO);

const isBasicInputValid = computed(() => {
  // Check basic inputs
  const basicValid = oldPassword.value !== '' && newPassword1.value !== '' && newPassword2.value !== '';

  // Ensure there is no feedback message
  const noFeedback = !feedbackMessage.value;

  return basicValid && noFeedback;
});

watch(() => [newPassword1.value, newPassword2.value], () => {
  if (newPassword1.value !== newPassword2.value) {
    feedbackMessage.value = "New passwords do not match!";
    feedbackType.value = MessageType.ERROR;
  } else if (oldPassword.value === newPassword1.value) {
    feedbackMessage.value = "New password should be different from the old password!";
    feedbackType.value = MessageType.WARNING;
  } else {
    feedbackMessage.value = '';
    feedbackType.value = MessageType.INFO;
  }
});

const handleChangePassword = async () => {
  if (feedbackMessage.value) return; // Abort if there's any feedback message (error or warning).
  console.log("csrfToken in changepasword", csrfToken.value);
  const response = await apiClient.changePassword(oldPassword.value, newPassword1.value, newPassword2.value, csrfToken.value);
  if (response && response.valid) {
    feedbackMessage.value = "Password changed successfully!";
    feedbackType.value = MessageType.SUCCESS;
    uiStore.setModalType(ModalTypes.INACTIVE);
  } else {
    feedbackMessage.value = response.error || "Failed to change password!";
    feedbackType.value = MessageType.ERROR;
  }
  csrfManager.fetchToken();
};

</script>
